import type { CookieDeclaration, CookieCategory } from './cookieConsent'
import { hasConsentableCookies } from './cookieConsent'
import { useCookieConsentStore } from './cookieConsent'
import { categoryDescription } from './cookieConsent'
import { categoryTitle } from './cookieConsent'
import { categories, useCookieDeclarations } from './cookieConsent'
import { Fragment, useState } from 'react'
import { Trans } from '@lingui/macro'
import { useBreakpointIndex } from '@jotta/ui/useBreakpointIndex'
import { CookieConsentModal } from './CookieConsentModal'
import dayjs from 'dayjs'
import { env } from '@jotta/utils/env'

function WideTable({ cookies }: { cookies: CookieDeclaration[] }) {
  return (
    <div className="body-sm-ln grid grid-cols-[1fr,1fr,2fr,1fr,1fr,1fr] gap-4">
      <div className="label-md">
        <Trans>Name</Trans>
      </div>
      <div className="label-md">
        <Trans>Vendor</Trans>
      </div>
      <div className="label-md">
        <Trans>Purpose</Trans>
      </div>
      <div className="label-md">
        <Trans>Expiry</Trans>
      </div>
      <div className="label-md">
        <Trans>Service</Trans>
      </div>
      <div className="label-md">
        <Trans>Privacy policy</Trans>
      </div>
      {cookies.map(
        ({
          name,
          data_processor_privacy_policy,
          domain,
          description,
          data_processor_name,
          expiration,
        }) => {
          return (
            <Fragment key={name}>
              <div className="break-all">{name}</div>
              <div className="break-all">{domain}</div>
              <div>{description}</div>
              <div>{expiration}</div>
              <div>{data_processor_name}</div>
              <div>
                <a
                  className="link break-all"
                  href={data_processor_privacy_policy}
                  target="_blank"
                  rel="noreferrer"
                >
                  {data_processor_privacy_policy}
                </a>
              </div>
            </Fragment>
          )
        },
      )}
    </div>
  )
}

function NarrowTable({ cookies }: { cookies: CookieDeclaration[] }) {
  return (
    <div className="grid grid-cols-[auto,1fr] items-center gap-4 break-words">
      {cookies.map(
        ({
          name,
          data_processor_privacy_policy,
          domain,
          description,
          data_processor_name,
          expiration,
        }) => {
          return (
            <Fragment key={name}>
              <div className="label-md">
                <Trans>Name</Trans>
              </div>
              <div>{name}</div>
              <div className="label-md">
                <Trans>Vendor</Trans>
              </div>
              <div>{domain}</div>
              <div className="label-md">
                <Trans>Purpose</Trans>
              </div>
              <div>{description}</div>
              <div className="label-md">
                <Trans>Expiry</Trans>
              </div>
              <div>{expiration}</div>
              <div className="label-md">
                <Trans>Service</Trans>
              </div>
              <div>{data_processor_name}</div>
              <div className="label-md">
                <Trans>Privacy policy</Trans>
              </div>
              <div>
                <a className="link" href={data_processor_privacy_policy}>
                  {data_processor_privacy_policy}
                </a>
              </div>
              <div />
              <div />
            </Fragment>
          )
        },
      )}
    </div>
  )
}

function Category({
  category,
  cookieDeclarations,
}: {
  category: CookieCategory
  cookieDeclarations: CookieDeclaration[]
}) {
  const breakpointIndex = useBreakpointIndex()

  const categoryCookies = cookieDeclarations
    .filter(cookie => cookie.category === category)
    .sort((a, b) => a.name.localeCompare(b.name))
  if (!categoryCookies.length) {
    return undefined
  }

  return (
    <div className="py-8">
      <header className="label-lg">{categoryTitle(category)}</header>
      <div className="py-4">{categoryDescription(category)}</div>

      {breakpointIndex > 1 ? (
        <WideTable cookies={categoryCookies} />
      ) : (
        <NarrowTable cookies={categoryCookies} />
      )}
    </div>
  )
}

function CookieStatement() {
  const hostname = window.location.hostname
  const buildTime = env.buildTime
  const statementUpdatedAtTimeFormatted = buildTime
    ? dayjs(buildTime).format('L')
    : undefined

  const [consent] = useCookieConsentStore()

  const clientUserId = consent?.clientUserId
  const contentTimestampFormatted = Math.random() < 10 ? undefined : undefined // TODO store in consent

  return (
    <div className="prose">
      <h1>
        <Trans>Cookie Statement</Trans>
      </h1>
      <p>
        <Trans>
          Most websites today use so-called cookies to improve the user
          experience. A cookie is a text file that is stored in your browser's
          memory when you visit or interacts with a website. Some cookies are
          necessary to deliver our website to you, while others are used to
          understand how people use our website, for marketing activities or for
          other purposes as described below.
        </Trans>
      </p>
      <p>
        <Trans>
          In accordance with Norwegian law, we are required to provide you with
          information about the use of cookies, and to obtain your consent if
          the use is not necessary for our website to work.
        </Trans>
      </p>
      <p>
        <Trans>
          On this page you will find information about how we use cookies on{' '}
          {hostname}
        </Trans>
      </p>
      <br />
      <h2>
        <Trans>Website owner and contact information</Trans>
      </h2>
      <p>
        <Trans>This website is operated and owned by:</Trans>
      </p>
      <dl>
        <dd>Jotta AS</dd>
        <dd>Øvre slottsgate 5</dd>
        <dd>0157 Oslo</dd>
        <dd>NORWAY</dd>
        <dd>
          <Trans>Phone:</Trans>{' '}
          <a className="link" href="tel:004721042900">
            (+47) 21 04 29 00
          </a>
        </dd>
        <dd>
          <Trans>Email:</Trans>{' '}
          <a className="link" href="mailto:support@jottacloud.com">
            support@jottacloud.com
          </a>
        </dd>
        <dd>
          <Trans>VAT number:</Trans> 992 603 615
        </dd>
      </dl>
      <br />
      <h2>
        <Trans>What is a cookie?</Trans>
      </h2>
      <p>
        <Trans>
          A cookie is a small data file stored in your computer, tablet or
          smartphone. A cookie is not a program that can contain harmful malware
          or virus.
        </Trans>
      </p>
      <br />
      <h2>
        <Trans>How our website uses cookies</Trans>
      </h2>{' '}
      <p>
        <Trans>
          Some cookies perform essential functions for our website. Cookies also
          help us get an overview of your visit to our website so we can
          continuously optimize and tailor the experience to your needs and
          interests. For example, cookies remember things like the items added
          to the shopping cart; whether you have visited our website before; if
          you are logged in; and the specific language and currency you prefer
          to see on the website. We also use cookies to target our ads
          specifically to you on other websites. In general, we use cookies as
          part of our service to present you with content that is as relevant to
          you as possible.
        </Trans>
      </p>
      <p>
        <Trans>
          You can see the specific services that store cookies and why they do
          it, under the different categories in the table below.
        </Trans>
      </p>
      <br />
      <h2>
        <Trans>How long are cookies stored?</Trans>
      </h2>
      <p>
        <Trans>
          The length of time a cookie is stored on your devices and browsers
          varies. The lifetime is calculated according to your last visit to the
          website. When a cookie expires, it is automatically deleted. All our
          cookies’ lifetimes are specified in our cookie policy.
        </Trans>
      </p>
      <br />
      <h2>
        <Trans>How to block or delete cookies?</Trans>
      </h2>
      <p>
        <Trans>
          You may at any time block all or just third-party cookies completely
          by changing the browser settings on your computer, tablet or
          smartphone. The location of these settings will depend on the browser
          you use. However, you should be aware that if you block all or just
          third-party cookies, there may be functions and services that you will
          be unable to use on the website (because these depend on cookies).
        </Trans>
      </p>
      <br />
      <h2>
        <Trans>How can you delete cookies?</Trans>
      </h2>
      <p>
        <Trans>
          It is easy to delete cookies that you have previously accepted. It
          depends on which browser (Chrome, Firefox, Safari, etc.) and device
          (smartphone, tablet, PC, Mac) you are using. You can typically find
          this information under settings – Security and Privacy – but this may
          vary from one browser to another.
        </Trans>
      </p>
      <p>
        <Trans>
          Remember: If you use more than one browser, you must delete cookies in
          all of them.
        </Trans>
      </p>
      <br />
      <h2>
        <Trans>Do you have any questions?</Trans>
      </h2>
      <p>
        <Trans>
          If you have any comments or questions relating to our information
          and/or processing of personal data, please feel welcome to contact us.
        </Trans>
      </p>
      <dl className="py-8">
        {clientUserId && (
          <dd>
            <Trans>User ID:</Trans> {clientUserId}{' '}
          </dd>
        )}
        {contentTimestampFormatted && (
          <dd>
            <Trans>Date:</Trans> {contentTimestampFormatted}
          </dd>
        )}
      </dl>
      <p>
        <Trans>
          The cookie policy was last updated on{' '}
          {statementUpdatedAtTimeFormatted}
        </Trans>
      </p>
      <br />
    </div>
  )
}

export function CookieDeclaration() {
  const cookieDeclarations = useCookieDeclarations()

  const openState = useState(false)
  return (
    <div className="mx-auto max-w-6xl p-8">
      <CookieStatement />

      {hasConsentableCookies(cookieDeclarations) && (
        <>
          <CookieConsentModal openState={openState} />
          <button
            className="btn btn-primary"
            onClick={() => openState[1](true)}
          >
            <Trans>Cookie Settings</Trans>
          </button>
        </>
      )}

      {categories.map(category => (
        <Category
          key={category}
          category={category}
          cookieDeclarations={cookieDeclarations}
        />
      ))}
    </div>
  )
}
